import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { AnalysisService } from '../services/analysis.service';
import { AnalysisDemographicFilters } from '../infrastructure/analysis.api.types';
import { Risk, RISK_COLOR, RISK_ORDER } from '@/app/utils/risk-colors';
import { NgxColor } from '@/app/shared/components/donut--chart-card/donut-chart.component';
import { AnalysisMode } from '../components/filters-report/reporte-filtros.component';

export type DemographicKey =
  | 'gender'
  | 'employeeType'
  | 'contractType'
  | 'educationStatus'
  | 'maritalStatus'
  | 'hasShiftRotation'
  | 'position'
  | 'ageRange'
  | 'seniority'
  | 'workSchedule'
  | 'educationLevel'
  | 'department'
  | 'occupation'
  | 'totalWorkExperience';

export type DonutSlice = { name: Risk; value: number };
export type DonutMini = { title: string; data: DonutSlice[] };
export type BarDatum = {
  name: string;
  series: { name: string; value: number }[];
};

export type BaseCardVM = {
  facet: DemographicKey | 'domainName' | 'categoryName' | 'finalAssessment';
  title: string;
  totalHint?: string;
  empty: boolean;
};

export type DonutCardVM = BaseCardVM & {
  kind: 'donut';
  data: DonutMini[];
  dataFlat: { name: string; value: number }[];
  colors?: NgxColor[];
};

export type BarCardVM = BaseCardVM & {
  kind: 'bar';
  data: BarDatum[];
  legendData: { name: string; value: string }[];
};

export type ChartCardVM = DonutCardVM | BarCardVM;

export const FACET_LABELS = new Map<DemographicKey, string>([
  ['gender', 'Género'],
  ['employeeType', 'Tipo de empleado'],
  ['contractType', 'Tipo de contrato'],
  ['educationStatus', 'Estatus educativo'],
  ['maritalStatus', 'Estado civil'],
  ['hasShiftRotation', 'Rotación de turno'],
  ['position', 'Puesto'],
  ['ageRange', 'Rango de edad'],
  ['seniority', 'Antigüedad en puesto'],
  ['workSchedule', 'Horario de trabajo'],
  ['educationLevel', 'Nivel educativo'],
  ['department', 'Área'],
  ['occupation', 'Ocupación'],
  ['totalWorkExperience', 'Experiencia laboral total'],
]);

export const ANALYSIS_MODE_LABELS = new Map<AnalysisMode, string>([
  ['final-assessments', 'Reporte general'],
  ['category-scores', 'Reporte por Categoría'],
  ['domain-scores', 'Reporte por Dominio'],
  ['dimension-scores', 'Reporte por Dimensión'],
]);

export const VALUE_TRANSLATIONS = new Map<DemographicKey, Map<string, string>>([
  [
    'gender',
    new Map([
      ['male', 'Masculino'],
      ['female', 'Femenino'],
      ['other', 'Otro'],
    ]),
  ],
  [
    'hasShiftRotation',
    new Map([
      ['true', 'Sí'],
      ['1', 'Sí'],
      ['yes', 'Sí'],
      ['si', 'Sí'],
      ['sí', 'Sí'],
      ['false', 'No'],
      ['0', 'No'],
      ['no', 'No'],
    ]),
  ],
]);

export function translateValue(facet: DemographicKey, value: string): string {
  const translationMap = VALUE_TRANSLATIONS.get(facet);
  if (!translationMap) {
    return value;
  }
  return translationMap.get(String(value).toLowerCase()) ?? value;
}

export function createScenarioLabel(
  facets: DemographicKey[],
  filters: Partial<AnalysisDemographicFilters>,
  analysisMode: AnalysisMode,
  guideId: 'guia-i' | 'guia-ii' | 'guia-iii'
): string {
  const parts: string[] = [];
  if (facets.length) {
    const translatedFacets = facets.map((f) => FACET_LABELS.get(f) ?? f);
    parts.push(`Agrupados por: <b>${translatedFacets.join(', ')}</b>`);
  }

  const keys = Object.keys(filters) as DemographicKey[];
  if (keys.length) {
    const translatedFilters = keys.map(k => {
      const keyLabel = FACET_LABELS.get(k) ?? k;
      const value = (filters as any)[k];
      const translatedValue = translateValue(k, value);
      return `${keyLabel} ${translatedValue}`;
    }).join(', ');
    parts.push(`Filtrados por <b>${translatedFilters}</b>`);
  }

  if (parts.length === 0) {
    if (guideId === 'guia-i' && analysisMode === 'domain-scores') {
      return 'Reporte';
    }
    return ` ${ANALYSIS_MODE_LABELS.get(analysisMode) ?? analysisMode}`;
  }

  return parts.join(' | ');
}

export function safeCategory(v: unknown): string | null {
    const s = String(v ?? '').trim();
    if (!s || s === 'null' || s === 'undefined') return null;
  const lowerS = s.toLowerCase();
  if (lowerS === 'true') return 'Sí';
  if (lowerS === 'false') return 'No';
    return s.charAt(0).toUpperCase() + s.slice(1);
}

export function readFilterValueForFacet(
  facet: DemographicKey,
  filters?: Partial<AnalysisDemographicFilters>
): string | undefined {
  if (!filters) return undefined;
  if (facet === 'hasShiftRotation') {
    const v = (filters as any).hasShiftRotation;
    const t = String(v).toLowerCase();
    if (['true', '1', 'sí', 'si', 'yes'].includes(t)) return 'Sí';
    if (['false', '0', 'no'].includes(t)) return 'No';
    return undefined;
  }
  const raw = (filters as any)[facet];
  return raw !== undefined && raw !== null && raw !== ''
    ? String(raw)
    : undefined;
}

export function createBaseTotal$(
  analysis: AnalysisService,
  campaignId$: Observable<string | null>,
  guideId$: Observable<string | null>
) {
  return combineLatest({
    camp: campaignId$.pipe(distinctUntilChanged()),
    guide: guideId$.pipe(distinctUntilChanged()),
  }).pipe(
    switchMap(({ camp, guide }) => {
      if (!camp || !guide) return of(0);
      return analysis
        .getFinalAssessments({ campaign: camp, guideId: guide })
        .pipe(
          map((rows) =>
            rows.reduce((acc, r) => acc + (r.totalRespondents || 0), 0)
          )
        );
    }),
    startWith(0),
    shareReplay({ bufferSize: 1, refCount: true })
  );
}

export function getFlatCounts(minis: DonutMini[]): Map<Risk, number> {
  const flatCounts = new Map<Risk, number>();
  for (const m of minis) {
    for (const s of m.data) {
      flatCounts.set(s.name, (flatCounts.get(s.name) ?? 0) + s.value);
    }
  }
  return flatCounts;
}

export function processCards(
  cards: ChartCardVM[],
  rows: any[]
): { donutCards: DonutCardVM[]; barCards: BarCardVM[]; isEmpty: boolean } {
  const donutCards = cards.filter(
    (c): c is DonutCardVM => c.kind === 'donut' && !c.empty
  );
  const barCards = cards.filter(
    (c): c is BarCardVM => c.kind === 'bar' && !c.empty
  );
  const isEmpty =
    rows.length === 0 || (donutCards.length === 0 && barCards.length === 0);
  return { donutCards, barCards, isEmpty };
}

export function getRiskCounts(
  rows: any[],
  rowRiskExtractor: (r: any) => Risk | null
): Map<Risk, number> {
  const counts = new Map<Risk, number>();
  for (const r of rows) {
    const risk = rowRiskExtractor(r);
    if (!risk) continue;
    const n = Number(r.totalRespondents ?? 0);
    counts.set(risk, (counts.get(risk) ?? 0) + n);
  }
  return counts;
}

export function calculateDonutMetrics(
  donutBuckets: Map<string, any[]>,
  rowRiskExtractor: (r: any) => Risk | null
) {
  const minis: DonutMini[] = [];
  for (const [bucket, bucketRows] of donutBuckets.entries()) {
    const counts = getRiskCounts(bucketRows, rowRiskExtractor);
    const data: DonutSlice[] = RISK_ORDER.map((risk) => ({
      name: risk,
      value: counts.get(risk) ?? 0,
    }));
    minis.push({ title: bucket, data });
  }

  const flatCounts = getFlatCounts(minis);
  const totalByRisk = RISK_ORDER.map((risk) => ({
    risk,
    count: flatCounts.get(risk) ?? 0,
  }));

  const subTotal = totalByRisk.reduce((acc, x) => acc + x.count, 0);
  const present = totalByRisk.filter((x) => x.count > 0);
  present.sort(
    (a, b) => RISK_ORDER.indexOf(a.risk) - RISK_ORDER.indexOf(b.risk)
  );

  return { minis, subTotal, present };
}

export function buildBarCard(
  base: {
    facet: DemographicKey | 'domainName' | 'categoryName' | 'finalAssessment';
    title: string;
  },
  rows: any[],
  byBucket: Map<string, any[]>,
  rowRiskExtractor: (r: any) => Risk | null,
  totalHintFn: (subTotal: number) => string
): BarCardVM {
  const data: BarDatum[] = [];
  const riskLevelsInUse = new Set<Risk>();
  const riskTotals = new Map<Risk, number>();
  // Nuevo: Map para almacenar conteos por bucket y riesgo
  const bucketRiskTotals = new Map<string, Map<Risk, number>>();
  

  rows.forEach((r) => {
    const risk = rowRiskExtractor(r);
    
    if (risk) riskLevelsInUse.add(risk);
  });
  

  for (const [bucket, bucketRows] of byBucket.entries()) {
    const seriesMap = getRiskCounts(bucketRows, rowRiskExtractor);
    
    // Inicializar el map para este bucket si no existe
    if (!bucketRiskTotals.has(bucket)) {
      bucketRiskTotals.set(bucket, new Map<Risk, number>());
    }
    
    for (const [risk, count] of seriesMap.entries()) {
      riskTotals.set(risk, (riskTotals.get(risk) ?? 0) + count);
      // Almacenar conteo por bucket y riesgo
      bucketRiskTotals.get(bucket)!.set(risk, count);
    }

    const series = RISK_ORDER.filter((risk) => riskLevelsInUse.has(risk)).map(
      (risk) => ({
        name: risk,
        value: seriesMap.get(risk) ?? 0,
      })
    );

    data.push({ name: bucket, series });
  }
  data.sort((a, b) => a.name.localeCompare(b.name, 'es'));

  const subTotal = data.reduce(
    (acc, group) =>
      acc + group.series.reduce((seriesAcc, s) => seriesAcc + s.value, 0),
    0
  );

  const legendData: { name: string; value: string }[] = [];
  
  // Obtener el label de la faceta para la leyenda
  const facetLabel = FACET_LABELS.get(base.facet as DemographicKey);
  const isDemographicFacet = facetLabel !== undefined;
  const isSpecialFacet = base.facet === 'domainName' || base.facet === 'categoryName';
  
  if ((isDemographicFacet || isSpecialFacet) && bucketRiskTotals.size > 0) {
    // Contar el número total de entradas individuales que se generarían
    let totalIndividualEntries = 0;
    for (const risk of RISK_ORDER) {
      if (riskLevelsInUse.has(risk)) {
        for (const [bucket, riskMap] of bucketRiskTotals.entries()) {
          const count = riskMap.get(risk) ?? 0;
          if (count > 0) {
            totalIndividualEntries++;
          }
        }
      }
    }
    
    // Si hay más de 20 entradas individuales, solo mostrar totales
    if (totalIndividualEntries > 20) {
      // Solo mostrar totales acumulados
      for (const risk of RISK_ORDER) {
        if (riskLevelsInUse.has(risk)) {
          const totalForRisk = riskTotals.get(risk) ?? 0;
          if (totalForRisk > 0) {
            legendData.push({
              name: `${totalForRisk} TOTAL con riesgo ${risk.toLowerCase()}`,
              value: RISK_COLOR[risk],
            });
          }
        }
      }
    } else {
      // Mostrar desglose detallado cuando hay 20 o menos entradas
      for (const risk of RISK_ORDER) {
        if (riskLevelsInUse.has(risk)) {
          for (const [bucket, riskMap] of bucketRiskTotals.entries()) {
            const count = riskMap.get(risk) ?? 0;
            if (count > 0) {
              let bucketLabel = bucket.toLowerCase();
              
              // Solo aplicar traducción para facetas demográficas
              if (isDemographicFacet) {
                bucketLabel = translateValue(base.facet as DemographicKey, bucket).toLowerCase();
              }
              
              legendData.push({
                name: `${count} de ${bucketLabel} con riesgo ${risk.toLowerCase()}`,
                value: RISK_COLOR[risk],
              });
            }
          }
          
          // Agregar total acumulado para este nivel de riesgo
          const totalForRisk = riskTotals.get(risk) ?? 0;
          if (totalForRisk > 0) {
            legendData.push({
              name: `${totalForRisk} TOTAL con riesgo ${risk.toLowerCase()}`,
              value: RISK_COLOR[risk],
            });
          }
        }
      }
    }
  } else {
    // Fallback al comportamiento original para facetas no demográficas
    for (const risk of RISK_ORDER) {
      const totalCount = riskTotals.get(risk) ?? 0;

      if (totalCount > 0) {
        legendData.push({
          name: `${totalCount} personas con riesgo ${risk.toLowerCase()}`,
          value: RISK_COLOR[risk],
        });
      }
    }
  }

  return {
    ...base,
    kind: 'bar',
    data,
    totalHint: totalHintFn(subTotal),
    empty: data.length === 0,
    legendData,
  };
}

export function buildDonutCard(
  base: {
    facet: DemographicKey | 'domainName' | 'categoryName' | 'finalAssessment';
    title: string;
  },
  donutBuckets: Map<string, any[]>,
  rowRiskExtractor: (r: any) => Risk | null,
  seriesFn: (
    present: { risk: Risk; count: number }[],
    minis: DonutMini[]
  ) => { name: string; value: number }[],
  totalHintFn: (subTotal: number) => string
): DonutCardVM {
  const { minis, subTotal, present } = calculateDonutMetrics(
    donutBuckets,
    rowRiskExtractor
  );
  const seriesForLegendAndChart = seriesFn(present, minis);

  const colors: NgxColor[] = present.map((x, index) => {
    const name = seriesForLegendAndChart[index].name;
    return { name, value: RISK_COLOR[x.risk] ?? '#999' };
  });

  const dataFlat = seriesForLegendAndChart.length
    ? seriesForLegendAndChart
    : [{ name: '0', value: 0 }];

  return {
    ...base,
    kind: 'donut',
    data: minis,
    dataFlat,
    colors,
    totalHint: totalHintFn(subTotal),
    empty: present.length === 0,
  };
}
